class SihaDepartments extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.scroller = this.querySelector('[data-siha-departments-scroller]');
    this.navigation = this.querySelector('[data-siha-departments-navigation]');
    this.cards = [...this.querySelectorAll('[data-siha-department-card]')];
    this.navigationButtons = [...this.querySelectorAll('[data-siha-department-button]')];

    if (!this.scroller || !this.cards.length) return;

    this.initialized = true;
    this.expandEnabled = this.dataset.expand === 'true';
    this.mobileMedia = window.matchMedia('(max-width: 749px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handlePointerOver = this.handlePointerOver.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleNavigationClick = this.handleNavigationClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.updateMobileActive = this.updateMobileActive.bind(this);

    this.scroller.addEventListener('pointerover', this.handlePointerOver);
    this.scroller.addEventListener('pointerleave', this.handlePointerLeave);
    this.scroller.addEventListener('focusin', this.handleFocusIn);
    this.scroller.addEventListener('focusout', this.handleFocusOut);
    this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });
    this.scroller.addEventListener('keydown', this.handleKeydown);
    this.navigation?.addEventListener('click', this.handleNavigationClick);

    if (this.mobileMedia.addEventListener) {
      this.mobileMedia.addEventListener('change', this.handleBreakpointChange);
    } else {
      this.mobileMedia.addListener(this.handleBreakpointChange);
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.updateMobileActive);
      this.resizeObserver.observe(this.scroller);
    }

    requestAnimationFrame(this.handleBreakpointChange);
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('pointerover', this.handlePointerOver);
    this.scroller?.removeEventListener('pointerleave', this.handlePointerLeave);
    this.scroller?.removeEventListener('focusin', this.handleFocusIn);
    this.scroller?.removeEventListener('focusout', this.handleFocusOut);
    this.scroller?.removeEventListener('scroll', this.handleScroll);
    this.scroller?.removeEventListener('keydown', this.handleKeydown);
    this.navigation?.removeEventListener('click', this.handleNavigationClick);
    this.resizeObserver?.disconnect();
    window.cancelAnimationFrame(this.scrollFrame);

    if (this.mobileMedia?.removeEventListener) {
      this.mobileMedia.removeEventListener('change', this.handleBreakpointChange);
    } else {
      this.mobileMedia?.removeListener(this.handleBreakpointChange);
    }

    this.initialized = false;
  }

  handlePointerOver(event) {
    if (!this.expandEnabled || this.mobileMedia.matches) return;

    const card = event.target.closest('[data-siha-department-card]');
    if (card && this.scroller.contains(card)) this.setDesktopActive(card);
  }

  handlePointerLeave() {
    if (!this.mobileMedia.matches) this.clearDesktopActive();
  }

  handleFocusIn(event) {
    if (!this.expandEnabled || this.mobileMedia.matches) return;

    const card = event.target.closest('[data-siha-department-card]');
    if (card) this.setDesktopActive(card);
  }

  handleFocusOut(event) {
    if (!this.mobileMedia.matches && !this.scroller.contains(event.relatedTarget)) {
      this.clearDesktopActive();
    }
  }

  handleScroll() {
    if (!this.mobileMedia.matches) return;

    window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(this.updateMobileActive);
  }

  handleNavigationClick(event) {
    const button = event.target.closest('[data-siha-department-button]');
    if (!button || !this.navigation.contains(button)) return;

    this.goToCard(Number(button.dataset.sihaDepartmentButton));
  }

  handleKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const currentIndex = this.cards.findIndex((card) =>
      this.mobileMedia.matches ? card.classList.contains('is-mobile-active') : card.classList.contains('is-active')
    );
    const startingIndex = currentIndex < 0 ? (direction > 0 ? -1 : this.cards.length) : currentIndex;
    const targetIndex = Math.min(Math.max(startingIndex + direction, 0), this.cards.length - 1);

    if (this.mobileMedia.matches) {
      this.goToCard(targetIndex);
    } else {
      this.cards[targetIndex].querySelector('a')?.focus();
      if (this.expandEnabled) this.setDesktopActive(this.cards[targetIndex]);
    }
  }

  handleBreakpointChange() {
    this.clearDesktopActive();

    if (this.mobileMedia.matches) {
      this.updateMobileActive();
    } else {
      this.cards.forEach((card) => card.classList.remove('is-mobile-active'));
    }
  }

  setDesktopActive(activeCard) {
    this.scroller.classList.add('has-active');
    this.cards.forEach((card) => card.classList.toggle('is-active', card === activeCard));
  }

  clearDesktopActive() {
    this.scroller.classList.remove('has-active');
    this.cards.forEach((card) => card.classList.remove('is-active'));
  }

  goToCard(index) {
    const card = this.cards[index];
    if (!card) return;

    const target = card.offsetLeft - (this.scroller.clientWidth - card.clientWidth) / 2;
    this.scroller.scrollTo({
      left: Math.max(target, 0),
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  updateMobileActive() {
    if (!this.mobileMedia.matches) return;

    const scrollerBox = this.scroller.getBoundingClientRect();
    const viewportCenter = scrollerBox.left + scrollerBox.width / 2;
    let activeIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.cards.forEach((card, index) => {
      const cardBox = card.getBoundingClientRect();
      const cardCenter = cardBox.left + cardBox.width / 2;
      const distance = Math.abs(cardCenter - viewportCenter);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        activeIndex = index;
      }
    });

    this.cards.forEach((card, index) => card.classList.toggle('is-mobile-active', index === activeIndex));
    this.navigationButtons.forEach((button, index) => {
      const isActive = index === activeIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  }
}

if (!customElements.get('siha-departments')) {
  customElements.define('siha-departments', SihaDepartments);
}


